import { Link } from "react-router-dom";
import { Bookmark, Star, MinusCircle, Ticket, Heart, CircleCheckBig } from "lucide-react";
import { useState, useRef, useEffect, memo } from "react";
import { useAuth } from "../lib/AuthContext";
import { useWatchStatus } from "../lib/WatchStatusContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import PlatformChip, { resolvePlatform, MultiPlatformChips } from "./PlatformChip";
import { prefetchTitleDetails } from "../lib/api";
import { watchProvidersCache } from "../lib/watchProvidersBatch";
import { buildTitleUrl, titleSlug as mkTitleSlug } from "../lib/utils";

// Build a BookMyShow search link — robust against BMS internal slug changes.
// Old /buytickets/{slug}/ deep-link showed "Technical error is inevitable".
function bmsDeepLink(title) {
  const query = encodeURIComponent((title || "").trim());
  const tag   = process.env.REACT_APP_BMS_AFFILIATE_TAG;
  const base  = `https://in.bookmyshow.com/search?q=${query}`;
  return tag ? `${base}&adid=${tag}` : base;
}

const trailerCache = new Map();
const imdbCache = new Map();
// Exported so MovieCarousel can pre-warm a whole row in ONE network call
// (was previously N×bulk-1 calls — 8 visible cards = 8 round-trips. Now
// it's 1 call for ~20 items, completing before any card even mounts).
export { imdbCache };

function MovieCard({ item, size = "md", minImdb = 0, showLessVariant = "hover", showPlatform = false, priority = false }) {
  const { user } = useAuth();
  const { isWatched: ctxWatched, isLoved: ctxLoved, toggleWatched, toggleLoved } = useWatchStatus();
  const [isWatched, setIsWatched] = useState(() => ctxWatched(item.media_type, item.tmdb_id));
  const [isLoved, setIsLoved] = useState(() => ctxLoved(item.media_type, item.tmdb_id));
  const [listPickerOpen] = useState(false); // kept for legacy refs, modal now global
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const widthCls = size === "lg" ? "w-44 sm:w-52" : "w-36 sm:w-44";
  const [trailerKey, setTrailerKey] = useState(null);
  const [showTrailer, setShowTrailer] = useState(false);
  const [imdbRating, setImdbRating] = useState(item.imdb_rating ?? null);
  const hoverTimerRef = useRef(null);
  // Multi-platform chips: null = not yet loaded, [] = no cache data, [{key,name}] = loaded
  const wpCacheKey = `${item.media_type}:${item.tmdb_id}`;
  const [watchPlatforms, setWatchPlatforms] = useState(
    showPlatform ? (watchProvidersCache.has(wpCacheKey) ? watchProvidersCache.get(wpCacheKey) : null) : null
  );

  useEffect(() => {
    if (!showPlatform) return;
    // Check if already in cache (batch pre-warm may have landed)
    if (watchProvidersCache.has(wpCacheKey)) {
      setWatchPlatforms(watchProvidersCache.get(wpCacheKey));
      return;
    }
    const onUpdate = () => {
      if (watchProvidersCache.has(wpCacheKey)) {
        setWatchPlatforms(watchProvidersCache.get(wpCacheKey));
      }
    };
    window.addEventListener("fm-watch-providers-update", onUpdate);
    return () => window.removeEventListener("fm-watch-providers-update", onUpdate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wpCacheKey, showPlatform]);

  // Per-card fallback: when the card becomes visible and the batch pre-warm
  // didn't populate providers (backend cache was cold), call the individual
  // watch-providers endpoint which does a live TMDB fetch + caches the result.
  useEffect(() => {
    if (!showPlatform || !item.tmdb_id || !item.media_type) return;
    // Theatre titles must NEVER show OTT provider badges — skip entirely.
    if (item.platform_key === "theatre") return;
    if (watchProvidersCache.has(wpCacheKey)) return; // batch already hit
    if (!cardRef.current) return;
    let cancelled = false;
    const io = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      if (watchProvidersCache.has(wpCacheKey)) {
        setWatchPlatforms(watchProvidersCache.get(wpCacheKey));
        return;
      }
      try {
        const r = await api.get(`/watch-providers/${item.media_type}/${item.tmdb_id}`);
        // Individual endpoint returns { providers: { flatrate, ads, free } }
        // Flatten to [{key, name}] to match the bulk-cards cache format
        const p = r.data?.providers || {};
        const providers = [...(p.flatrate || []), ...(p.ads || []), ...(p.free || [])]
          .filter((x) => x?.key)
          .map(({ key, name }) => ({ key, name }));
        // Dedupe by key (flatrate + ads + free may overlap)
        const seen = new Set();
        const unique = providers.filter((x) => seen.has(x.key) ? false : seen.add(x.key));
        if (!cancelled) {
          watchProvidersCache.set(wpCacheKey, unique);
          setWatchPlatforms(unique);
        }
      } catch {
        // silently skip — card degrades to single-chip or no badge
      }
    }, { rootMargin: "200px" });
    io.observe(cardRef.current);
    return () => { cancelled = true; io.disconnect(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wpCacheKey, showPlatform]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Lazy IMDb rating lookup using IntersectionObserver
  const cardRef = useRef(null);
  const [filmistaniScore, setFilmistaniScore] = useState(item.filmistani_score);
  const [provisionalScore, setProvisionalScore] = useState(item.provisional_score ?? false);
  const [risingGem, setRisingGem] = useState(item.rising_gem ?? false);
  useEffect(() => {
    // Return early only when rating AND badge flags are already fully resolved.
    // If imdb_rating is set but provisional_score/rising_gem are absent from the
    // rail payload (e.g. trending-india), we must stay alive to catch the
    // fm-imdb-cache-update event fired by the imdbBatch row pre-warm.
    if (imdbRating != null && item.provisional_score != null && item.rising_gem != null) return;
    const cacheKey = `${item.media_type}-${item.tmdb_id}`;
    // First — check cache (row pre-warm may have already populated it).
    if (imdbCache.has(cacheKey)) {
      const cached = imdbCache.get(cacheKey);
      // If the cached entry is from before v3.57 (no provisional_score field), treat as stale.
      if (cached && "provisional_score" in cached) {
        setImdbRating(cached?.rating ?? null);
        if (cached?.filmistani_score) setFilmistaniScore(cached.filmistani_score);
        if (cached?.provisional_score != null) setProvisionalScore(cached.provisional_score);
        if (cached?.rising_gem != null) setRisingGem(cached.rising_gem);
        return;
      }
      // Stale entry — fall through to re-fetch below
      imdbCache.delete(cacheKey);
    }
    // Listen for row-level pre-warm completion — re-check cache when fired.
    const onCacheUpdate = () => {
      if (imdbCache.has(cacheKey)) {
        const cached = imdbCache.get(cacheKey);
        if (!cached || !("provisional_score" in cached)) return; // Still stale, ignore
        setImdbRating(cached?.rating ?? null);
        if (cached?.filmistani_score) setFilmistaniScore(cached.filmistani_score);
        if (cached?.provisional_score != null) setProvisionalScore(cached.provisional_score);
        if (cached?.rising_gem != null) setRisingGem(cached.rising_gem);
      }
    };
    window.addEventListener("fm-imdb-cache-update", onCacheUpdate);

    if (!cardRef.current) return () => window.removeEventListener("fm-imdb-cache-update", onCacheUpdate);
    // Per-card fallback fetch (only fires for items beyond the row pre-warm cap).
    const io = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        io.disconnect();
        if (imdbCache.has(cacheKey)) {
          const cached = imdbCache.get(cacheKey);
          // Only use if it's the new format (has provisional_score field)
          if (cached && "provisional_score" in cached) {
            setImdbRating(cached?.rating ?? null);
            if (cached?.filmistani_score) setFilmistaniScore(cached.filmistani_score);
            if (cached?.provisional_score != null) setProvisionalScore(cached.provisional_score);
            if (cached?.rising_gem != null) setRisingGem(cached.rising_gem);
            return;
          }
          imdbCache.delete(cacheKey); // Stale — force re-fetch
        }
        try {
          const r = await api.post("/imdb-bulk", {
            items: [{
              media_type: item.media_type, tmdb_id: item.tmdb_id,
              vote_average: item.vote_average, vote_count: item.vote_count,
              popularity: item.popularity, original_language: item.original_language,
              genre_names: item.genre_names, release_year: item.release_year,
              release_date: item.release_date || item.first_air_date || null,
            }]
          });
          const data = r.data?.ratings?.[0] || {};
          const rating = data.imdb_rating ?? null;
          const fs = data.filmistani_score;
          imdbCache.set(cacheKey, {
            rating, filmistani_score: fs,
            provisional_score: data.provisional_score ?? false,
            rising_gem: data.rising_gem ?? false,
          });
          setImdbRating(rating);
          if (fs) setFilmistaniScore(fs);
          if (data.provisional_score != null) setProvisionalScore(data.provisional_score);
          if (data.rising_gem != null) setRisingGem(data.rising_gem);
        } catch (e) {
          imdbCache.set(cacheKey, { rating: null });
        }
      }
    }, { rootMargin: "200px" });
    io.observe(cardRef.current);
    return () => {
      io.disconnect();
      window.removeEventListener("fm-imdb-cache-update", onCacheUpdate);
    };
  }, [item.tmdb_id, item.media_type, imdbRating, item.vote_average, item.vote_count, item.popularity, item.original_language, item.genre_names, item.release_year]);

  // v2.16 — Removed viewport-triggered prefetch of /api/title. It fired
  // for every card scrolling past on Home → 8+ unnecessary /title calls
  // competing with real rail data and delaying first card paint by ~3s.
  // Optimistic UI (v2.14) makes the click feel instant even without
  // prefetch. Hover-prefetch (desktop only) still fires via onMouseEnter.

  const onMarkWatched = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Sign in to mark as watched"); return; }
    const next = !isWatched;
    setIsWatched(next);
    await toggleWatched(item.media_type, item.tmdb_id, item);
    toast.success(next ? `Marked "${item.title}" as watched` : `Removed watched mark`);
  };

  const onToggleLoved = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Sign in to love this title"); return; }
    const next = !isLoved;
    setIsLoved(next);
    await toggleLoved(item.media_type, item.tmdb_id, item);
    toast.success(next ? `Added "${item.title}" to Loved ❤️` : `Removed from Loved`);
  };

  const onOpenListPicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      let attempts = 0;
      let l2Dismissed = false;
      try {
        attempts = parseInt(sessionStorage.getItem("fm.guestSaveAttempts") || "0", 10) || 0;
        l2Dismissed = sessionStorage.getItem("fm.subtleTrialJustDismissed") === "1";
        sessionStorage.setItem("fm.guestSaveAttempts", String(attempts + 1));
      } catch (_e) { /* ignore */ }
      if (attempts >= 1 && !l2Dismissed && !sessionStorage.getItem("fm.l3TrialFired")) {
        try { sessionStorage.setItem("fm.l3TrialFired", "1"); } catch (_e) { /* ignore */ }
        toast.success("Sign up to save your watchlist & try Pro free for 7 days", {
          duration: 8000,
          action: { label: "Start trial →", onClick: () => { window.location.href = "/login?next=/?trial=1"; } },
        });
      } else {
        toast.error("Sign in to save to your watchlist");
      }
      return;
    }
    // Open global SaveToListModal (portal at root level — not cramped inside card)
    window.dispatchEvent(new CustomEvent("fm-open-save-modal", { detail: { item } }));
  };

  const onShowLess = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const genres = (item.genre_names || []).slice(0, 2).filter(Boolean);
    // v1.74 — Smart Show-Less (Option B):
    //   Backend always HIDES this specific title (deterministic) AND
    //   increments a dismiss-counter for its genres. A genre only gets
    //   soft-demoted when ≥5 dismisses with 0 clicks. Re-engaging with
    //   the genre auto-resets the demotion.
    // Optimistic UX: fire card-removal event immediately, save in background.
    window.dispatchEvent(new CustomEvent("fm-show-less-card", {
      detail: { tmdb_id: item.tmdb_id, media_type: item.media_type, genres },
    }));
    try {
      await api.post("/me/show-less", {
        tmdb_id: item.tmdb_id,
        media_type: item.media_type,
        genres,
      });
      toast.success(`Got it. We'll show fewer like "${item.title}"`, {
        description: "You'll keep seeing acclaimed titles in this genre.",
      });
    } catch (err) {
      toast.error("Couldn't save preference (but card was removed locally).");
    }
  };

  // Fire-and-forget click tracking (works for both authed & anon users).
  // Backend uses this to learn taste preferences from passive browsing.
  const onCardClick = () => {
    try {
      api.post("/track/click", {
        tmdb_id: item.tmdb_id,
        media_type: item.media_type,
        genre_ids: item.genre_ids || [],
        genre_names: item.genre_names || [],
        original_language: item.original_language || null,
      }).catch(() => { /* fire-and-forget */ });
    } catch (e) { /* never block navigation */ }
  };

  const onMouseEnter = () => {
    // Fire backend prefetch IMMEDIATELY so by the time the user clicks the
    // detail page is in L1/L2 cache. No 900ms wait. Deduped per session.
    prefetchTitleDetails(item.media_type, item.tmdb_id);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(async () => {
      const cacheKey = `${item.media_type}-${item.tmdb_id}`;
      if (trailerCache.has(cacheKey)) {
        const key = trailerCache.get(cacheKey);
        if (key) { setTrailerKey(key); setShowTrailer(true); }
        return;
      }
      try {
        const r = await api.get(`/trailer/${item.media_type}/${item.tmdb_id}`);
        const key = r.data?.key || null;
        trailerCache.set(cacheKey, key);
        if (key) { setTrailerKey(key); setShowTrailer(true); }
      } catch (e) {
        trailerCache.set(cacheKey, null);
      }
    }, 900);
  };

  const onMouseLeave = () => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    setShowTrailer(false);
  };

  const testIdSlug = (item.title || "movie").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);

  // Trust filter: hide card if IMDb is known AND below threshold
  if (minImdb > 0 && imdbRating != null && imdbRating < minImdb) {
    return null;
  }
  // While IMDb is still loading and we're filtering, dim the card slightly to hint loading
  const trustPending = minImdb > 0 && imdbRating == null;

  // "Why this?" tooltip text, driven by the backend `_reason` annotation
  const REASON_LABELS = {
    taste:   "Matches your taste",
    banger:  "🏆 Universal must-watch",
    stretch: "🌱 Outside your usual — but worth a try",
    daily:   "Today's editorial pick",
  };
  const reasonText = REASON_LABELS[item._reason];

  return (
    <Link
      to={buildTitleUrl(item.media_type, item.title, item.release_year, item.tmdb_id)}
      state={{
        // v2.14 — Optimistic UI: pass the card's known data via router state so
        // the detail page can render instantly with title/poster/IMDb/score
        // BEFORE the /api/title fetch lands. The API call still fires, but
        // by then the user is already looking at a fully-painted page.
        seed: {
          media_type: item.media_type,
          tmdb_id: item.tmdb_id,
          _slug: mkTitleSlug(item.title || "", item.release_year || ""),  // slug seed for MovieDetails validation
          title: item.title,
          poster_path: item.poster_path,
          backdrop_path: item.backdrop_path,
          release_year: item.release_year,
          vote_average: item.vote_average,
          vote_count: item.vote_count,
          imdb_rating: imdbRating ?? item.imdb_rating,
          imdb_votes: item.imdb_votes,
          filmistani_score: filmistaniScore ?? item.filmistani_score,
          genre_names: item.genre_names,
          tags: item.tags,
          original_language: item.original_language,
        },
      }}
      data-testid={`movie-card-${testIdSlug}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onCardClick}
      ref={cardRef}
      className={`fm-card group relative ${widthCls} flex-shrink-0 block ${trustPending ? "opacity-50" : ""}`}
    >
      <div className="relative aspect-[2/3] overflow-hidden rounded-md bg-neutral-900">
        {item.poster_path && !imgError ? (
          <>
            {!imgLoaded && (
              <div className="fm-skel absolute inset-0 z-[1]" aria-hidden="true" />
            )}
            <img
              src={item.poster_path}
              srcSet={item.poster_path && item.poster_path.includes("/w342/")
                ? `${item.poster_path.replace("/w342/", "/w185/")} 185w, ${item.poster_path} 342w`
                : undefined}
              sizes="(max-width: 640px) 144px, 176px"
              alt={item.title}
              width={size === "lg" ? 208 : 176}
              height={size === "lg" ? 312 : 264}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              decoding="async"
              ref={(el) => { if (el?.complete && el.naturalWidth > 0) setImgLoaded(true); }}
              onLoad={() => setImgLoaded(true)}
              onError={() => { setImgError(true); setImgLoaded(true); }}
              className={`fm-card-img w-full h-full object-cover relative z-[2] ${imgLoaded ? "fm-fade-in" : "opacity-0"}`}
            />
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-2 text-center bg-neutral-900">
            <span className="text-neutral-400 text-[11px] font-medium leading-tight line-clamp-4">{item.title}</span>
            {item.release_year && <span className="text-neutral-600 text-[10px]">{item.release_year}</span>}
          </div>
        )}

        {/* Trailer hover-preview */}
        {showTrailer && trailerKey && (
          <div className="absolute inset-0 z-20 fm-fade pointer-events-none" data-testid={`trailer-preview-${testIdSlug}`}>
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1`}
              title="trailer"
              allow="autoplay; encrypted-media"
              frameBorder="0"
            />
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent" />
          </div>
        )}

        {/* score badge */}
        {filmistaniScore > 0 && (
          <div className="absolute top-2 left-2 z-30">
            <span className="fm-score" data-testid="filmistani-score">{filmistaniScore}</span>
            {provisionalScore && (
              <div
                className="text-[9px] font-semibold text-amber-300/90 leading-none mt-0.5 text-center whitespace-nowrap"
                data-testid="provisional-badge"
                title="Score is provisional. Fan votes dominate in opening week — rating typically settles after 3 weeks."
              >
                ⚡ Early Rating
              </div>
            )}
          </div>
        )}

        {/* IMDb chip — always visible on cards when known */}
        {imdbRating != null && (
          <div className="absolute top-2 right-2 z-30" data-testid="imdb-chip">
            <span className="px-2 py-0.5 rounded text-[11px] font-bold font-mono-fm bg-yellow-400/95 text-black flex items-center gap-1">
              ★ {imdbRating}
            </span>
          </div>
        )}

        {/* v2.42 — "NEW" badge for titles released in the last 30 days.
            Sits below the IMDb chip (top-right). Helps users spot fresh
            content across ANY rail without having to navigate to
            Recently Released. Robust: derived purely from the title's
            release_date / first_air_date — auto-rolls off after 30 days
            so we never need a cron to expire it. */}
        {(() => {
          const rd = item.release_date || item.first_air_date;
          if (!rd) return null;
          const releaseTime = new Date(rd).getTime();
          if (Number.isNaN(releaseTime)) return null;
          const now = Date.now();
          const days = (now - releaseTime) / (1000 * 60 * 60 * 24);
          if (days < 0 || days > 30) return null;
          return (
            <div
              className={`absolute ${imdbRating != null ? "top-9" : "top-2"} right-2 z-30`}
              data-testid="new-release-badge"
            >
              <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono-fm bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-sm uppercase tracking-wider">
                NEW
              </span>
            </div>
          );
        })()}

        {/* Watched overlay badge */}
        {isWatched && (
          <div className="absolute inset-0 z-20 bg-black/50 flex items-center justify-center pointer-events-none rounded-lg">
            <div className="flex flex-col items-center gap-1">
              <CircleCheckBig className="w-8 h-8 text-green-400 drop-shadow-lg" />
              <span className="text-[10px] font-bold text-green-300 tracking-wider uppercase">Watched</span>
            </div>
          </div>
        )}

        {/* Hover overlay - hidden during trailer */}
        {!showTrailer && (
          <div className="fm-card-overlay absolute inset-0 p-3 flex flex-col justify-end bg-gradient-to-t from-black via-black/80 to-transparent z-10">
            <div className="flex items-center gap-2 mb-1.5">
              {filmistaniScore > 0 && <span className="fm-score">{filmistaniScore}</span>}
              {imdbRating != null ? (
                <span className="flex items-center gap-1 text-xs text-yellow-300 font-mono-fm font-semibold">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /> IMDb {imdbRating}
                </span>
              ) : null}
            </div>
            <h3 className="font-display font-semibold text-sm leading-tight line-clamp-2">{item.title}</h3>
            <div className="text-[11px] text-neutral-400 mt-0.5">
              {item.release_year} • {item.genre_names?.slice(0,2).join(" • ")}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {risingGem && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30"
                  data-testid="rising-gem-badge"
                  title="Organic word-of-mouth growth — high quality, not fan-driven."
                >
                  ◆ Rising Gem
                </span>
              )}
              {item.extended_run && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30"
                  data-testid="extended-run-badge"
                  title="Still going strong — extended theatrical run."
                >
                  ★ Extended Run
                </span>
              )}
              {item.tags?.slice(0,2).map((t) => (
                <span key={t} className="fm-tag">{t}</span>
              ))}
              {/* Book Tickets — theatrical titles only */}
              {item.platform_key === "theatre" && (
                <a
                  href={bmsDeepLink(item.title)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`bms-btn-${item.tmdb_id}`}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#E50914]/80 text-white hover:bg-[#E50914] transition-colors"
                >
                  <Ticket className="w-2.5 h-2.5" /> Book
                </a>
              )}
            </div>
          </div>
        )}

        {/* OTT platform chip removed in v1.71 — coverage was incomplete (only
            ~30% of cards stamped via the _OTT_INDEX), so the chips appeared
            inconsistent. User direction: "either on all or none — go for
            none". The clean colored chip stays on the Movie Detail page. */}

        {/* Action buttons — loved ❤️ / watched ✓ / save-to-list */}
        <div className={`absolute ${imdbRating != null ? "top-9" : "top-2"} right-2 z-30 flex flex-col gap-1 transition-opacity opacity-0 group-hover:opacity-100`}>
          <button
            onClick={onMarkWatched}
            data-testid={`watched-btn-${testIdSlug}`}
            title={isWatched ? "Remove watched mark" : "Mark as watched"}
            className={`p-1.5 rounded-full border transition-colors ${isWatched ? "bg-green-600 border-green-500 text-white" : "bg-black/70 border-white/15 hover:bg-green-600 text-white"}`}
          >
            <CircleCheckBig className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onToggleLoved}
            data-testid={`loved-btn-${testIdSlug}`}
            title={isLoved ? "Remove from Loved" : "Love this"}
            className={`p-1.5 rounded-full border transition-colors ${isLoved ? "bg-rose-600 border-rose-500 text-white" : "bg-black/70 border-white/15 hover:bg-rose-600 text-white"}`}
          >
            <Heart className={`w-3.5 h-3.5 ${isLoved ? "fill-current" : ""}`} />
          </button>
          <button
            onClick={onOpenListPicker}
            data-testid={`save-btn-${testIdSlug}`}
            title="Save to list"
            className="p-1.5 rounded-full bg-black/70 hover:bg-[#E50914] border border-white/15 transition-colors text-white"
          >
            <Bookmark className="w-3.5 h-3.5" />
          </button>
        </div>
        {/* v1.76: Hover-variant minus icon REMOVED globally per user feedback
            ("remove minus from all cards"). Show-less is now ONLY available
            on personalized rails (For-You + Because-You-Loved) via the
            inline button below year/type. */}
      </div>

      {/* Reason chip — "Why this?" subtle text above the title (was previously
          absolute-positioned and overlapped with the title block — fixed per
          user feedback). Shown inline so it never collides. */}
      <div className="mt-2 px-0.5">
        {reasonText && (
          <div
            className="text-[10px] uppercase tracking-wider text-neutral-400 truncate mb-0.5"
            data-testid={`reason-${testIdSlug}`}
          >
            {reasonText}
          </div>
        )}
        <h4 className="font-display text-sm font-semibold text-white truncate">{item.title}</h4>
        <p className="text-[11px] text-neutral-500 truncate">
          {item.release_year} • {item.media_type === "tv" ? "Series" : "Movie"}
        </p>
        {/* v1.97 — Platform chip ("Now Streaming" rails only). Opt-in via
            showPlatform prop so evergreen rails (Top All-Time, Classics) stay
            clean. Multi-platform (watchProvidersBatch) takes priority when the
            batch cache has loaded; otherwise falls back to single chip from
            item.platform_key. Both silently skip when no data. */}
        {showPlatform && (
          <div className="mt-1 flex">
            {watchPlatforms?.length > 0 ? (
              item.watch_link ? (
                <a href={item.watch_link} target="_blank" rel="noopener noreferrer"
                   onClick={e => e.stopPropagation()}
                   className="hover:opacity-80 transition-opacity"
                   title={`Watch on ${resolvePlatform(item)?.name || "streaming platform"}`}>
                  <MultiPlatformChips platforms={watchPlatforms} />
                </a>
              ) : (
                <MultiPlatformChips platforms={watchPlatforms} />
              )
            ) : watchPlatforms === null && resolvePlatform(item) ? (
              item.watch_link ? (
                <a href={item.watch_link} target="_blank" rel="noopener noreferrer"
                   onClick={e => e.stopPropagation()}
                   className="hover:opacity-80 transition-opacity">
                  <PlatformChip item={item} size="xs" />
                </a>
              ) : (
                <PlatformChip item={item} size="xs" />
              )
            ) : !watchPlatforms?.length && resolvePlatform(item) ? (
              item.watch_link ? (
                <a href={item.watch_link} target="_blank" rel="noopener noreferrer"
                   onClick={e => e.stopPropagation()}
                   className="hover:opacity-80 transition-opacity">
                  <PlatformChip item={item} size="xs" />
                </a>
              ) : (
                <PlatformChip item={item} size="xs" />
              )
            ) : null}
          </div>
        )}
        {/* Show-less button (INLINE VARIANT) — text + icon below year/type.
            Used on For-You + Because-You-Loved so it's visible & deliberate,
            not hover-accidental. */}
        {showLessVariant === "inline" && (
          <button
            onClick={onShowLess}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-200 transition-colors"
            data-testid={`show-less-inline-${testIdSlug}`}
            title="Show less like this"
          >
            <MinusCircle className="w-3 h-3" />
            <span>Show less</span>
          </button>
        )}
      </div>
    </Link>
  );
}

// Memoize to prevent re-renders when parent state changes (e.g. language filter toggle)
// Cards only need to re-render if their item data or display props actually change.
export { MovieCard as MovieCardBase };
export default memo(MovieCard, (prev, next) =>
  prev.item?.tmdb_id === next.item?.tmdb_id &&
  prev.size === next.size &&
  prev.showPlatform === next.showPlatform
);
